import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { formatInTimeZone } from 'date-fns-tz'
import { useApi } from '../hooks/useApi'
import { api } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import LoadingSpinner from '../components/common/LoadingSpinner'
import ErrorMessage from '../components/common/ErrorMessage'
import ShowEventMap from '../components/common/ShowEventMap'
import ConfirmationModal from '../components/common/ConfirmationModal'
import SocialShareLinks from '../components/common/SocialShareLinks'
import { TrashIcon, PencilIcon, CheckIcon, XMarkIcon, ClockIcon } from '@heroicons/react/24/outline'
import { usePageTitle } from '../hooks/usePageTitle'
import { idFromSlug, eventEditPath, newSalePath, salePath, saleEditPath } from '../utils/slugs'

const formatDateInTimezone = (dateString, timezone) => {
  if (!dateString) return dateString
  const tz = timezone || 'UTC'
  return formatInTimeZone(new Date(dateString), tz, 'MMMM d, yyyy')
}

const getTimezoneAbbr = (dateString, timezone) => {
  if (!dateString || !timezone) return ''
  return formatInTimeZone(new Date(dateString), timezone, 'zzz')
}

const getMapImageUrl = (location) => {
  if (!location?.latitude || !location?.longitude) return null
  const mapboxToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN
  if (!mapboxToken) return null

  const width = 800
  const height = 400
  const zoom = 14
  const markerColor = '#ef4444' // Red color for sale markers
  const marker = `pin-s+${markerColor.replace('#', '')}(${location.longitude},${location.latitude})`
  
  return `https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/${marker}/${location.longitude},${location.latitude},${zoom},0/${width}x${height}?access_token=${mapboxToken}`
}

export default function EventShowPage() {
  const { id: slug } = useParams()
  const id = idFromSlug(slug)
  const navigate = useNavigate()
  const eventApi = useApi()
  const salesApi = useApi()
  const [selectedSale, setSelectedSale] = useState(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleteEventModalOpen, setDeleteEventModalOpen] = useState(false)
  const [saleToDelete, setSaleToDelete] = useState(null)
  const { user, isAdmin } = useAuth()

  const eventData = eventApi.data
  const eventTitle = eventData?.name ? `${eventData.name} — ${eventData.location?.city}, ${eventData.location?.state}` : 'Event'
  const eventDescription = eventData?.description?.substring(0, 160) ||
    (eventData?.location ? `Garage sale event in ${eventData.location.city}, ${eventData.location.state}` : '')
  usePageTitle(eventTitle, {
    description: eventDescription,
    ogImage: eventData?.photo_url
  })

  useEffect(() => {
    if (!id) return

    const loadData = async () => {
      try {
        const event = await eventApi.execute(() => api.events.get(id))
        if (event) {
          await salesApi.execute(() => api.events.getSales(id))
        }
      } catch (error) {
        console.error('Error loading event data:', error)
      }
    }

    loadData()
  }, [id]) // Only depend on id

  const handleDeleteClick = (e, sale) => {
    e.preventDefault()
    e.stopPropagation()
    setSaleToDelete(sale)
    setDeleteModalOpen(true)
  }

  const handleDeleteConfirm = async () => {
    try {
      await api.sales.delete(saleToDelete.id)
      setDeleteModalOpen(false)
      setSaleToDelete(null)
      await salesApi.execute(() => api.events.getSales(id))
    } catch (error) {
      console.error('Error deleting sale:', error)
    }
  }

  const handleDeleteEvent = async () => {
    try {
      await api.events.delete(id)
      setDeleteEventModalOpen(false)
      navigate('/events')
    } catch (error) {
      console.error('Error deleting event:', error)
    }
  }

  const handleApprove = async (e, saleId) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      await api.sales.approve(saleId)
      await salesApi.execute(() => api.events.getSales(id))
    } catch (error) {
      console.error('Error approving sale:', error)
    }
  }

  const handleReject = async (e, saleId) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      await api.sales.reject(saleId)
      await salesApi.execute(() => api.events.getSales(id))
    } catch (error) {
      console.error('Error rejecting sale:', error)
    }
  }

  if (eventApi.loading || salesApi.loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  if (eventApi.error) {
    return (
      <ErrorMessage
        message={eventApi.error.message}
        onRetry={() => eventApi.execute(() => api.events.get(id))}
        className="max-w-7xl mx-auto mt-8"
      />
    )
  }

  if (salesApi.error) {
    return (
      <ErrorMessage
        message={salesApi.error.message}
        onRetry={() => salesApi.execute(() => api.events.getSales(id))}
        className="max-w-7xl mx-auto mt-8"
      />
    )
  }

  if (!eventApi.data) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <ErrorMessage
          message="Event not found"
          className="max-w-7xl mx-auto mt-8"
        />
      </div>
    )
  }

  const event = eventApi.data
  const sales = salesApi.data || []
  const isEventOwner = user && event.user?.id === user.id
  const canEditEvent = user && (isEventOwner || isAdmin)
  const canDeleteEvent = user && (isEventOwner || isAdmin)
  const canApprove = isEventOwner || isAdmin
  const pendingSales = sales.filter(s => s.approval_status === 'pending')

  // Prepare sharing data
  const currentUrl = window.location.href
  const shareTitle = `${event.name} - Garage Sale Event`
  const shareDescription = `Join us for ${event.name} in ${event.location?.city}, ${event.location?.state}. ${formatDateInTimezone(event.start_date, event.timezone)} - ${formatDateInTimezone(event.end_date, event.timezone)}`
  const shareHashtags = ['garagesale', 'event', 'deals', 'community']

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Hero Photo */}
      {event.photo_url && (
        <div className="mb-8 rounded-xl overflow-hidden border border-border shadow-sm">
          <img
            src={event.photo_url}
            alt={event.name}
            className="w-full h-[300px] sm:h-[400px] object-cover"
          />
        </div>
      )}

      <div className="mb-8">
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-text">{event.name}</h1>
            <p className="mt-2 text-text-secondary">
              {formatDateInTimezone(event.start_date, event.timezone)} - {formatDateInTimezone(event.end_date, event.timezone)}
            </p>
            {event.location && (
              <p className="mt-1 text-text-secondary">
                {event.location.city}, {event.location.state}
              </p>
            )}
            <p className="mt-1 text-text-secondary">
              Organized by {event.user?.name || 'Anonymous'}
            </p>
            {event.description && (
              <div className="mt-4">
                <p className="text-text-secondary whitespace-pre-wrap">{event.description}</p>
              </div>
            )}
          </div>
          {canEditEvent && (
            <div className="flex gap-2 flex-shrink-0">
              <Link
                to={eventEditPath(event)}
                className="btn-primary btn-sm"
              >
                <PencilIcon className="h-4 w-4" />
                Edit Event
              </Link>
              {canDeleteEvent && (
                <button
                  onClick={() => setDeleteEventModalOpen(true)}
                  className="btn-danger btn-sm"
                >
                  <TrashIcon className="h-4 w-4" />
                  Delete Event
                </button>
              )}
            </div>
          )}
        </div>

        {/* Social Share Links */}
        <div className="border-t border-border pt-4">
          <SocialShareLinks
            url={currentUrl}
            title={shareTitle}
            description={shareDescription}
            hashtags={shareHashtags}
            className="justify-center sm:justify-start"
          />
        </div>
      </div>

      {/* Map View */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-text">Sales Map</h2>
          {event.location && (
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${event.location.latitude},${event.location.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-2 min-h-[44px]
                text-sm font-medium text-primary-600 hover:text-primary-700
                hover:bg-primary-50 dark:hover:bg-primary-50 rounded-lg transition-colors"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Get Directions
            </a>
          )}
        </div>
        <ShowEventMap
          className="h-[400px] sm:h-[500px] rounded-xl overflow-hidden border border-border"
          eventLocation={event.location}
          radius={event.radius}
          sales={sales}
          onSaleSelect={setSelectedSale}
        />
      </div>

      {/* Grid View */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-text">Sales</h2>
            {canApprove && event.requires_approval && pendingSales.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium
                bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                <ClockIcon className="h-3.5 w-3.5" />
                {pendingSales.length} pending
              </span>
            )}
          </div>
          <Link
            to={newSalePath(event)}
            className="btn-primary btn-sm"
          >
            Add Sale
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {sales.map((sale) => {
            const canDeleteSale = user && (sale.user?.id === user.id || event.user?.id === user.id || isAdmin)
            const canEditSale = user && (sale.user?.id === user.id || isAdmin)
            const isPending = sale.approval_status === 'pending'
            const isRejected = sale.approval_status === 'rejected'

            return (
              <div
                key={sale.id}
                className={`card-hover overflow-hidden border-2 cursor-pointer relative ${
                  selectedSale?.id === sale.id ? 'border-primary-500'
                    : isPending ? 'border-amber-300 dark:border-amber-600'
                    : isRejected ? 'border-red-300 dark:border-red-600 opacity-60'
                    : 'border-transparent hover:border-border-strong'
                }`}
                onClick={() => setSelectedSale(sale)}
              >
                {/* Approval status badge */}
                {event.requires_approval && sale.approval_status && sale.approval_status !== 'approved' && (
                  <div className="absolute top-2 left-2 z-10">
                    {isPending && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium
                        bg-amber-100 text-amber-800 shadow-sm">
                        <ClockIcon className="h-3 w-3" />
                        Pending
                      </span>
                    )}
                    {isRejected && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium
                        bg-red-100 text-red-800 shadow-sm">
                        <XMarkIcon className="h-3 w-3" />
                        Rejected
                      </span>
                    )}
                  </div>
                )}

                <Link to={salePath(event, sale)} className="block">
                  <div className="aspect-w-16 aspect-h-9">
                    {(sale.primary_photo_url || sale.photos?.[0]) ? (
                      <img
                        src={sale.primary_photo_url || sale.photos[0].url}
                        alt={sale.title}
                        className="object-cover w-full h-full"
                      />
                    ) : sale.location && (
                      <img
                        src={getMapImageUrl(sale.location)}
                        alt={`Map of ${sale.location.address}`}
                        className="object-cover w-full h-full"
                      />
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex justify-between items-start">
                      <h3 className="text-lg font-medium text-text hover:text-primary-600 transition-colors">{sale.title}</h3>
                      <div className="flex gap-1 ml-2">
                        {canEditSale && (
                          <Link
                            to={saleEditPath(event, sale)}
                            onClick={(e) => e.stopPropagation()}
                            className="btn-icon min-h-[44px] min-w-[44px] p-2 rounded-full"
                            title="Edit sale"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </Link>
                        )}
                        {canDeleteSale && (
                          <button
                            onClick={(e) => handleDeleteClick(e, sale)}
                            className="btn-icon min-h-[44px] min-w-[44px] p-2 rounded-full
                              hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-50"
                            title="Delete sale"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="mt-1 text-sm text-text-muted">{sale.location.address}</p>
                    <p className="mt-2 text-sm text-text-secondary line-clamp-2">{sale.description}</p>
                    {isAdmin && sale.user?.id !== user.id && (
                      <p className="mt-2 text-xs text-info-600 font-medium">
                        By: {sale.user?.name || 'Anonymous'}
                      </p>
                    )}
                  </div>
                </Link>

                {/* Approve/Reject buttons for event owner */}
                {canApprove && (isPending || isRejected) && (
                  <div className="px-4 pb-4 flex gap-2">
                    <button
                      onClick={(e) => handleApprove(e, sale.id)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2
                        text-sm font-medium text-white bg-green-600 hover:bg-green-700
                        rounded-lg transition-colors min-h-[44px]"
                    >
                      <CheckIcon className="h-4 w-4" />
                      Approve
                    </button>
                    {isPending && (
                      <button
                        onClick={(e) => handleReject(e, sale.id)}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2
                          text-sm font-medium text-white bg-red-600 hover:bg-red-700
                          rounded-lg transition-colors min-h-[44px]"
                      >
                        <XMarkIcon className="h-4 w-4" />
                        Reject
                      </button>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {sales.length === 0 && (
          <div className="empty-state">
            <p className="empty-state-title">No sales yet</p>
            <p className="empty-state-description">No sales have been added to this event yet. Be the first!</p>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false)
          setSaleToDelete(null)
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Sale"
        message="Are you sure you want to delete this sale? This action cannot be undone."
      />

      <ConfirmationModal
        isOpen={deleteEventModalOpen}
        onClose={() => setDeleteEventModalOpen(false)}
        onConfirm={handleDeleteEvent}
        title="Delete Event"
        message="Are you sure you want to delete this event? This will also delete all associated sales. This action cannot be undone."
        confirmText="Delete Event"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
    </div>
  )
} 